import React, {useEffect, useRef, useState} from "react";
import {useLoading} from "../components/loading/LoadingProvider";
import {
    Form,
    FormControl,
    FormGroup,
    FormLabel,
    Button, Row, Col, Image,
} from "react-bootstrap";
import {post, postUpload} from "../utils/FetchUtil";
import "../../style/template/gift_message_template_view.scss";
import {useDevice} from "../components/device/DeviceProvider.tsx";
import ResizeImageUploader from "../components/image/ResizeImageUploader.tsx";
import GiftCategorySwiper from "../components/swiper/GiftCategorySwiper.tsx";
import MessageSamplesSwiper from "../components/swiper/MessageSamplesSwiper.tsx";
import FormatGoodsSwiper from "../components/swiper/FormatGoodsSwiper.tsx";
import {img} from "../utils/ImageUtil.ts";
import {isEmpty, isNotEmpty} from "../utils/CommonUtil.ts";
import {popErrors} from "../utils/MessageUtil.ts";
import OnlineMsgTemplateModal from "../modal/OnlineMsgTemplateModal.tsx";
import {replaceNewLineHtml} from "../utils/HtmlUtil.ts";

interface GiftMessageTemplateViewProps{
    orderNumber?: string;
    orderDetailNumber?: number;
    handleSaved: () => void;
    handleCanceled: () => void;
}
const GiftMessageTemplateView: React.FC<GiftMessageTemplateViewProps> = (
    {
        orderNumber = null,
        orderDetailNumber = null,
        handleSaved = () => {},
        handleCanceled = () => {}
    }
) => {
    const templateKbn: string = "20";
    const { isMobile } = useDevice();
    const { showLoading, hideLoading } = useLoading();
    const [validated, setValidated] = useState(false);
    const [giftCategoryList, setGiftCategoryList] = useState<GiftCategoryDto[]>([]);
    const [selectedCategory, setSelectedCategory] = useState<GiftCategoryDto>({
        code: "",
        name: "すべて"
    });
    useEffect( () => {
        showLoading();
        post<GiftCategoryDto>({
            action: "/api/template/online_msg/input_templates",
            data: selectedCategory
        })
            .then((data) => {
                if(data){
                    if(data.format_goods_list){
                        setFormatGoodsList(data.format_goods_list);
                    }else{
                        setFormatGoodsList([]);
                    }
                    if(data.sample_message_list){
                        setSampleMessageList(data.sample_message_list);
                    }else{
                        setSampleMessageList([]);
                    }
                }else {
                    setFormatGoodsList([]);
                    setSampleMessageList([]);
                }
            })
            .catch((reason) => {
                console.log(reason);
                setFormatGoodsList([]);
                setSampleMessageList([]);
            })
            .finally(() => {
                hideLoading();
            });
    }, [selectedCategory]);
    const [formatGoodsList, setFormatGoodsList] = useState<FormatGoodsDto[]>([]);
    const [selectedFormatGoods, setSelectedFormatGoods] = useState<FormatGoodsDto | null>(null);
    useEffect(() => {
        if(selectedFormatGoods){
            if(!sampleMessageList || sampleMessageList.length == 0 || selectedMsgSample != null){
                if(!isMobile){
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                }
            }
        }
    }, [selectedFormatGoods]);
    const [sampleMessageList, setSampleMessageList] = useState<SampleMessageDto[]>([]);
    const [selectedMsgSample, setSelectedMsgSample] = useState<SampleMessageDto | null>(null);
    useEffect(() => {
        if(selectedMsgSample){
            setMessageContent(selectedMsgSample.sample_message);
            if(!formatGoodsList || formatGoodsList.length == 0 || selectedFormatGoods != null){
                if(!isMobile){
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                }
            }
        }
    }, [selectedMsgSample]);
    const [messageContent, setMessageContent] = useState("");
    const messageRef = useRef(null);
    const [selectedImageFile, setSelectedImageFile] = useState<Blob | null>(null);
    const [senderName, setSenderName] = useState("");
    const [showMsgTempModal, setShowMsgTempModal] = useState(false);
    useEffect( () => {
        showLoading();
        post<GiftCategoryDto[]>({
            action: "/api/template/online_msg/gift_categories",
            data: {}
        })
            .then((data) => {
                if(data){
                    setGiftCategoryList(data);
                }else {
                    setGiftCategoryList([]);
                }
            })
            .catch((reason) => {
                console.log(reason);
                setGiftCategoryList([]);
            })
            .finally(() => {
                hideLoading();
            });
    }, []);
    useEffect( () => {
        const textarea = messageRef.current;
        if (textarea) {
            // @ts-ignore
            textarea.style.height = 'auto';
            // @ts-ignore
            const lineCount = (textarea.value.match(/\n/g) || []).length + 1;
            if (lineCount > 10) {
                // @ts-ignore
                textarea.style.height = `${textarea.scrollHeight}px`;
            } else {
                // @ts-ignore
                textarea.style.height = 'auto';
            }
        }
    }, [messageContent]);
    const onClickCancel = () => {
        handleCanceled();
    };
    const handleSelectedCategory = (selectedItem: GiftCategoryDto) => {
        setSelectedCategory(selectedItem);
    }
    const handleSelectedFormatGoods = (selectedDto : FormatGoodsDto)=> {
        setSelectedFormatGoods(selectedDto);
        setSelectedImageFile(null);
    };
    const handleSelectedMsgSample = (selectedDto : SampleMessageDto)=> {
        setSelectedMsgSample(selectedDto);
    };
    const onClickDeleteImage = () => {
        setSelectedImageFile(null);
        setSelectedFormatGoods(null);
    }
    const handleImageSelected = (imgBlob: Blob | null) => {
        setSelectedImageFile(imgBlob);
        setSelectedFormatGoods(null);
    };
    const handleSubmitTemplateFromModal = ( selectedGoods: FormatGoodsDto | null, selectedMsg: SampleMessageDto | null ) => {
        if(selectedGoods != null){
            setSelectedFormatGoods(selectedGoods);
            setSelectedImageFile(null);
        }
        if(selectedMsg != null){
            setSelectedMsgSample(selectedMsg);
        }
        setShowMsgTempModal(false);
    }
    const createImageBlobUrl = (imgBlob: Blob | null): string | undefined => {
        if(imgBlob){
            return URL.createObjectURL(imgBlob);
        }
        return undefined;
    }
    const onClickSave = () => {
        if(validInput()){
            showLoading();
            postUpload<any>({
                action: "/api/template/entry/apply_site_msg",
                data: createEntryFormData()
            })
                .then((data) => {
                    if(data !== null){
                        handleSaved();
                    }
                })
                .catch((reason) => {
                    console.log(reason);
                    popErrors(reason.message);
                })
                .finally(() => {
                    hideLoading();
                });
        }
    };
    const validInput = () => {
        setValidated(false);
        if(selectedImageFile == null && selectedFormatGoods == null && isEmpty(messageContent) && isEmpty(senderName)){
            popErrors("画像またはメッセージをご入力ください。");
            return false;
        }
        let frmInput = document.getElementById("giftMsgInputForm");
        if(frmInput){
            // @ts-ignore
            if(!frmInput.checkValidity()){
                setValidated(true);
                return false;
            }
        }
        return true;
    }
    const createEntryFormData = () => {
        let templateDto = {
            template_kbn: templateKbn,
            order_number: orderNumber,
            order_detail_number: orderDetailNumber,
            template_name: "マイデザインオンラインメッセージ",
            image_path: selectedFormatGoods != null && isNotEmpty(selectedFormatGoods.goods_image_path) ? selectedFormatGoods.goods_image_path : null
        };
        let templateDetails = [];
        if(selectedImageFile != null){
            templateDetails.push({option_id: 109, option_detail_id: -1});
        }else if(selectedFormatGoods != null){
            templateDetails.push({option_id: 109, option_detail_id: -1, upload_image_path: selectedFormatGoods.goods_image_path});
        }
        templateDetails.push({option_id: 108, option_detail_id: -1, input_text: messageContent});
        templateDetails.push({option_id: 106, option_detail_id: -1, input_text: senderName});
        const formData = new FormData();
        formData.append('form_json', JSON.stringify({template: templateDto, detail_list: templateDetails}));
        if(selectedImageFile != null){
            formData.append('file', selectedImageFile);
            formData.append('file_name', "message_image.jpg");
        }
        return formData;
    };
    return (
        <section className="mb-5">
            <h2 className="template-title">
                オンライン・メッセージ登録（無料）<br/>
                {giftCategoryList && giftCategoryList.length != 0 && (
                    <span style={{letterSpacing: '0'}}>※テンプレートもご用意しております、ぜひご利用ください。</span>
                )}
            </h2>
            <br className="pc"/>
            <Form id="giftMsgInputForm" noValidate validated={validated} className="p-0">
                <Row className="gx-0">
                    <Col xs={12} className="sp text-center mt-4 mb-4">
                        <Button variant="success"
                                onClick={() => {setShowMsgTempModal(true)}}
                        >テンプレートの利用 ▶</Button>
                    </Col>
                    <Col xs={12} md={6} className="msg-preview-col">
                        <FormGroup>
                            <FormLabel>
                                <strong>1. 画像の登録</strong>
                                <span className="text-danger"
                                      style={{marginLeft: '3rem'}}
                                >※pngもしくはjpegファイル</span>
                            </FormLabel>
                            <FormLabel>
                                <span className="input-request">解像度 幅1200px以上をご用意ください。※推奨比率4：3</span>
                                {(selectedFormatGoods !== null || selectedImageFile !== null) && (
                                    <Button variant="danger"
                                            className="float-end pc"
                                            onClick={onClickDeleteImage}
                                    >画像クリア</Button>
                                )}
                            </FormLabel>
                        </FormGroup>
                        <div className="input-preview-area pc">
                            {(selectedImageFile !== null || (selectedFormatGoods === null && true))  && (
                                <ResizeImageUploader resize_width={1200}
                                                     min_width={1200}
                                                     min_height={900}
                                                     selectedFile={selectedImageFile}
                                                     handleSelectedImage={handleImageSelected}
                                />
                            )}
                            {selectedFormatGoods !== null && (
                                <Image src={img(selectedFormatGoods.goods_image_path)} fluid />
                            )}
                            <p className="preview-msg"
                               dangerouslySetInnerHTML={{ __html: replaceNewLineHtml(messageContent)}}
                            />
                            <p className="preview-sender">{senderName}</p>
                        </div>
                        <div className="msg-image-sp sp">
                            {(selectedImageFile !== null || (selectedFormatGoods === null && true))  && (
                                <ResizeImageUploader resize_width={1200}
                                                     min_width={1200}
                                                     min_height={900}
                                                     selectedFile={selectedImageFile}
                                                     handleSelectedImage={handleImageSelected}
                                />
                            )}
                            {selectedFormatGoods !== null && (
                                <Image src={img(selectedFormatGoods.goods_image_path)} fluid />
                            )}
                            {(selectedFormatGoods !== null || selectedImageFile !== null) && (
                                <FormGroup className="mt-3">
                                    <FormLabel>
                                        <Button variant="danger"
                                                className="float-end"
                                                onClick={onClickDeleteImage}
                                        >画像クリア</Button>
                                    </FormLabel>
                                </FormGroup>
                            )}
                        </div>
                    </Col>
                    <Col xs={12} md={6} className="msg-input-col">
                        <FormGroup className="mt-4">
                            <FormLabel>
                                <strong>2. メッセージのご入力</strong>
                                <span className="float-end">{messageContent.length}/2000</span>
                            </FormLabel>
                            <FormControl as="textarea"
                                         style={{ overflowY: 'hidden', resize: 'none' }}
                                         ref={messageRef}
                                         rows={10}
                                         placeholder=""
                                         maxLength={2000}
                                         value={messageContent}
                                         onChange={(e) => setMessageContent(e.target.value)}
                            ></FormControl>
                            <Form.Control.Feedback type="invalid">
                                メッセージを入力してください。
                            </Form.Control.Feedback>
                        </FormGroup>
                        <br className="pc"/>
                        <FormGroup className="mt-4">
                            <FormLabel className="input-title">
                                <strong>3. お名前ご入力</strong>
                                <span className="float-end">{senderName.length}/50</span>
                            </FormLabel>
                            <FormControl type="text"
                                         placeholder=""
                                         maxLength={50}
                                         value={senderName}
                                         onChange={(e) => setSenderName(e.target.value)}
                            />
                            <Form.Control.Feedback type="invalid">
                                お名前を入力してください。
                            </Form.Control.Feedback>
                        </FormGroup>
                    </Col>
                    <Col xs={12} className="msg-preview-col sp">
                        <FormGroup className="mt-4">
                            <FormLabel className="input-title">
                                <strong>4. プレビュー</strong>
                            </FormLabel>
                        </FormGroup>
                        <div className="input-preview-area">
                            {selectedImageFile === null &&
                                selectedFormatGoods === null &&
                                isEmpty(messageContent) &&
                                isEmpty(senderName) && (
                                    <p className="preview-msg text-danger">
                                        ＜入力はございません＞
                                    </p>
                            )}
                            {selectedImageFile !== null  && (
                                <Image src={createImageBlobUrl(selectedImageFile)} fluid />
                            )}
                            {selectedFormatGoods !== null && (
                                <Image src={img(selectedFormatGoods.goods_image_path)} fluid />
                            )}
                            <p className="preview-msg"
                               dangerouslySetInnerHTML={{ __html: replaceNewLineHtml(messageContent)}}
                            />
                            <p className="preview-sender">{senderName}</p>
                        </div>
                    </Col>
                </Row>
            </Form>
            <br className="pc"/>
            <div className="buttons-area mt-5">
                <Button variant="light"
                        onClick={onClickCancel}
                >戻る</Button>
                <Button variant="secondary"
                        onClick={onClickSave}
                >保　存</Button>
            </div>
            {giftCategoryList && giftCategoryList.length != 0 && (
                <div id="useMsgTemplateArea" className="pc">
                    <Row className="gx-0">
                        <Col xs={12}>
                            <h3 className="text-center mt-5">テンプレートの利用</h3>
                            <div className="gift-category-list">
                                <GiftCategorySwiper categoryList={giftCategoryList}
                                                    handleSelected={handleSelectedCategory}
                                />
                            </div>
                        </Col>
                        {formatGoodsList && formatGoodsList.length !== 0 && (
                            <Col xs={12}>
                                <h4>画像テンプレートから選ぶ</h4>
                                <div className="format-goods-list">
                                    <FormatGoodsSwiper goodsList={formatGoodsList}
                                                       handleSelected={handleSelectedFormatGoods}
                                    />
                                </div>
                            </Col>
                        )}
                        {sampleMessageList && sampleMessageList.length !== 0 && (
                            <Col xs={12}>
                                <h4>メッセージテンプレートから選ぶ</h4>
                                <div className="sample-msg-list">
                                    <MessageSamplesSwiper sampleList={sampleMessageList}
                                                          showName={true}
                                                          handleSelected={handleSelectedMsgSample}
                                    />
                                </div>
                            </Col>
                        )}
                    </Row>
                </div>
            )}
            {isMobile &&
                <OnlineMsgTemplateModal categoryList={giftCategoryList}
                                        show={showMsgTempModal}
                                        handleClose={() => {setShowMsgTempModal(false)}}
                                        handleSubmit={handleSubmitTemplateFromModal}
                />
            }
        </section>
    );
};
export default GiftMessageTemplateView;